import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Chip,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
  Link,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions, canAccessResource, getCurrentUser } from "../../utils/permissions";
import DiaCircle from "../DiaCircle";
import GaugeChip from "../GaugeChip";
import { formatInteger, truncateText, PROGRAM_NAME_MAX_WIDTH } from "../../utils/formatters";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface DeliveryDetail {
  id: number;
  dia: string;
  gauge: string;
  rolls: number;
  weight: number;
}

interface Delivery {
  id: number;
  company: {
    id: number;
    name: string;
  };
  program: {
    id: number;
    lot: string;
  };
  delivery_person_name: string;
  delivery_person_phone: string;
  delivery_vehicle_number: string;
  total_rolls?: number;
  total_weight?: number;
  calculated_total_rolls: number;
  calculated_total_weight: number | string;
  specs_display: string;
  dias_display: string;
  gauges_display: string;
  delivery_details: DeliveryDetail[];
  created_at: string;
}

interface Machine {
  id: number;
  dia: number;
  gauge: string;
  machine_type: string;
}

export default function DeliveryList() {
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { canCreate } = usePermissions();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { translations: t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const user = getCurrentUser();
      const canFetchMachines = canAccessResource(user, 'machines');
      
      // Fetch deliveries first
      const deliveriesResponse = await authenticatedFetch(`${API_URL}/deliveries?page=${page}&per_page=25`);

      if (!deliveriesResponse.ok) {
        throw new Error("Failed to fetch deliveries");
      }

      const deliveriesData = await deliveriesResponse.json();
      setDeliveries(deliveriesData);
      
      // Get pagination info from headers
      const totalPagesHeader = deliveriesResponse.headers.get('X-Total-Pages');
      if (totalPagesHeader) {
        setTotalPages(parseInt(totalPagesHeader));
      }

      // Only fetch machines for users who have access (admin/employee)
      if (canFetchMachines) {
        const machinesResponse = await authenticatedFetch(`${API_URL}/machines`);
        if (machinesResponse.ok) {
          const machinesData = await machinesResponse.json();
          setMachines(machinesData);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to find machine_type for a given dia/gauge combination
  // If there are multiple machine types for the same dia/gauge, return the first one
  const findMachineType = (dia: string, gauge: string): string => {
    const machine = machines.find(m => 
      Number(m.dia) === Number(dia) && m.gauge === gauge
    );
    return machine?.machine_type || 'fine'; // Default to 'fine' if not found
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Card sx={{ mt: { xs: 2, sm: 4 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {canCreate('deliveries') && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => navigate("/deliveries/new")}
                sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: '160px' } }}
              >
                {t.deliveries.createNew}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {isMobile ? (
            // Mobile Card View
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {deliveries.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  {t.deliveries.noDeliveries}
                </Typography>
              ) : (
                deliveries.map((delivery) => (
                  <Card 
                    key={delivery.id}
                    onClick={() => navigate(`/deliveries/${delivery.id}`)}
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box>
                          <Typography variant="h6" component="div">
                            Delivery #{delivery.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(delivery.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Chip
                          label={truncateText(delivery.program?.lot || "No Lot", 20)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ maxWidth: PROGRAM_NAME_MAX_WIDTH.chipMobile }}
                          title={delivery.program?.lot || "No Lot"}
                        />
                      </Stack>
                      
                      <Link
                        component="button"
                        variant="body1"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/companies/${delivery.company?.id}`);
                        }}
                        sx={{ 
                          color: 'primary.main', 
                          fontWeight: 500, 
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                          display: 'block',
                          textAlign: 'left',
                          mb: 1,
                        }}
                      >
                        {delivery.company?.name}
                      </Link>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Dia:</Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {delivery.delivery_details
                              .filter((detail, index, self) => 
                                index === self.findIndex((d) => 
                                  d.dia === detail.dia && d.gauge === detail.gauge
                                )
                              )
                              .map((detail, idx) => (
                                <DiaCircle
                                  key={idx}
                                  dia={detail.dia}
                                  gauge={detail.gauge}
                                  machineType={findMachineType(detail.dia, detail.gauge)}
                                  machines={machines}
                                  size="small"
                                />
                              ))}
                          </Box>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">Gauge:</Typography>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {(delivery.gauges_display || delivery.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                              .split(", ")
                              .filter(g => g)
                              .map((gauge, idx) => (
                                <GaugeChip
                                  key={idx}
                                  gauge={gauge}
                                />
                              ))}
                          </Box>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Total Rolls:</Typography>
                          <Chip
                            label={formatInteger(delivery.calculated_total_rolls)}
                            size="small"
                            color="success"
                          />
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Total Weight:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {typeof delivery.calculated_total_weight === "number"
                              ? delivery.calculated_total_weight.toFixed(2)
                              : Number(delivery.calculated_total_weight || 0).toFixed(2)} kg
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Program Lot</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Dia</TableCell>
                    <TableCell>Gauge (GG)</TableCell>
                    <TableCell>Total Rolls</TableCell>
                    <TableCell>Total Weight (kg)</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {deliveries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t.deliveries.noDeliveries}
                    </TableCell>
                  </TableRow>
                ) : (
                  deliveries.map((delivery) => (
                    <TableRow
                      key={delivery.id}
                      onClick={() => navigate(`/deliveries/${delivery.id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <TableCell>{delivery.id}</TableCell>
                      <TableCell>
                        {new Date(delivery.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/companies/${delivery.company?.id}`);
                          }}
                          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {delivery.company?.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={truncateText(delivery.program?.lot || "No Lot", 25)}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ maxWidth: PROGRAM_NAME_MAX_WIDTH.chip }}
                          title={delivery.program?.lot || "No Lot"}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {delivery.delivery_details
                            .filter((detail, index, self) => 
                              index === self.findIndex((d) => 
                                d.dia === detail.dia && d.gauge === detail.gauge
                              )
                            )
                            .map((detail, idx) => (
                              <DiaCircle
                                key={idx}
                                dia={detail.dia}
                                gauge={detail.gauge}
                                machineType={findMachineType(detail.dia, detail.gauge)}
                                machines={machines}
                                size="small"
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(delivery.gauges_display || delivery.specs_display?.split('/')[1]?.replace(/ GG/g, '') || '')
                            .split(", ")
                            .filter(g => g)
                            .map((gauge, idx) => (
                              <GaugeChip
                                key={idx}
                                gauge={gauge}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatInteger(delivery.calculated_total_rolls)}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell>
                        {typeof delivery.calculated_total_weight === "number"
                          ? delivery.calculated_total_weight.toFixed(2)
                          : Number(
                              delivery.calculated_total_weight || 0
                            ).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

